import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { FloatingParticles } from './FloatingParticles';
import { useMousePosition } from '@/hooks/useMousePosition';
import { ChevronDown, Sparkles, Diamond } from 'lucide-react';

export const HeroSection = () => {
  const ref = useRef(null);
  const mouse = useMousePosition();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  const mouseX = typeof window !== 'undefined' ? (mouse.x / window.innerWidth - 0.5) * 20 : 0;
  const mouseY = typeof window !== 'undefined' ? (mouse.y / window.innerHeight - 0.5) * 20 : 0;

  const scrollToProgram = () => {
    const el = document.querySelector('#program');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Parallax */}
      <motion.div
        style={{ scale }}
        className="absolute inset-0 z-0"
      >
        <img
          src="https://images.unsplash.com/photo-1532926381893-7542290edf1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3ZWxsbmVzc3xlbnwwfHx8fDE3NzU3MzYzODB8MA&ixlib=rb-4.1.0&q=85"
          alt="Luxury wellness"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 30%, hsl(40 76% 44% / 0.08), transparent 70%)' }} />
      </motion.div>

      {/* Floating Particles */}
      <FloatingParticles count={25} />

      {/* Decorative Orbs */}
      <motion.div
        style={{ y: y1 }}
        className="absolute top-1/4 left-[15%] w-64 h-64 rounded-full opacity-20"
      >
        <div
          className="w-full h-full rounded-full animate-pulse-glow"
          style={{
            background: 'radial-gradient(circle, hsl(var(--gold) / 0.3), transparent)',
            transform: `translate(${mouseX * 0.5}px, ${mouseY * 0.5}px)`,
          }}
        />
      </motion.div>

      <motion.div
        style={{ y: y2 }}
        className="absolute bottom-1/4 right-[15%] w-48 h-48 rounded-full opacity-15"
      >
        <div
          className="w-full h-full rounded-full animate-pulse-glow"
          style={{
            background: 'radial-gradient(circle, hsl(var(--tier-bloom) / 0.4), transparent)',
            transform: `translate(${mouseX * -0.3}px, ${mouseY * -0.3}px)`,
            animationDelay: '1.5s',
          }}
        />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity, y: y2 }}
        className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-20"
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="inline-flex items-center gap-2 mb-8 px-5 py-2.5 rounded-full border border-gold/20 bg-gold/5 backdrop-blur-sm"
        >
          <Diamond className="w-4 h-4 text-gold" />
          <span className="font-body text-sm tracking-wider text-gold">LOYALTY REIMAGINED</span>
        </motion.div>

        {/* Title with light elegant effects */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          style={{ transform: `translate(${mouseX * 0.1}px, ${mouseY * 0.1}px)` }}
        >
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-medium leading-tight mb-2">
            <span className="text-foreground">Radiance</span>
          </h1>
          <motion.h1
            className="font-display text-5xl sm:text-6xl lg:text-8xl font-medium leading-tight mb-6"
            animate={{ opacity: [0.85, 1, 0.85] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <span className="text-shimmer-gold italic">Rewards</span>
          </motion.h1>
        </motion.div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="font-body text-base lg:text-lg text-cream-soft max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Every treatment is a step toward a more radiant you. Earn Glow Points,
          unlock exclusive benefits, and experience luxury that rewards your devotion to self-care.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            onClick={() => {
              const el = document.querySelector('#join');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-gold hover:bg-gold-light text-accent-foreground font-body text-sm tracking-wide px-8 h-12 rounded-full transition-all duration-300 hover:shadow-gold-lg group"
          >
            <Sparkles className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:rotate-12" />
            Start Your Glow Journey
          </Button>
          <Button
            variant="outline"
            onClick={scrollToProgram}
            className="border-gold/30 text-gold hover:bg-gold/10 hover:text-gold hover:border-gold/50 font-body text-sm tracking-wide px-8 h-12 rounded-full bg-background/30 backdrop-blur-sm transition-all duration-300"
          >
            Explore the Program
          </Button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 flex flex-wrap justify-center gap-8 lg:gap-16"
        >
          {[
            { value: '5', label: 'Exclusive Tiers' },
            { value: '20%', label: 'Max Discount' },
            { value: '5x', label: 'Point Multiplier' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-display text-3xl lg:text-4xl text-gold font-medium">{stat.value}</div>
              <div className="font-body text-xs tracking-wider text-cream-soft/70 mt-1 uppercase">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <button onClick={scrollToProgram} className="flex flex-col items-center gap-2 text-cream-soft/60 hover:text-gold transition-colors duration-300">
          <span className="font-body text-xs tracking-widest uppercase">Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            <ChevronDown size={20} />
          </motion.div>
        </button>
      </motion.div>
    </section>
  );
};
